import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import { findMemberBySlug } from "./congressionalConfig";
import { useHouseTradesByName } from "./hooks/useHouseTradesByName";
import { useSenateTradesByName } from "./hooks/useSenateTradesByName";
import {
  isMemberTrade,
  memberSurname,
  newestTradesFirst,
  tradeDirection,
} from "./congressionalTradeData";
import { roundedPanelSx } from "../Dashboard/overviewPanel";
import { CongressionalTradeTable } from "./components/CongressionalTradeTable";
import { getPersonPortraitImageProps } from "./personPortrait";
import {
  DisclosurePagination,
  DisclosureSummary,
  InvestorWorkspace,
  SectionTitle,
  WorkspaceHeader,
  WorkspaceState,
} from "./components/InvestorWorkspace";

const CongressionalMemberDetail = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { memberName } = useParams<{ memberName: string }>();
  const member = findMemberBySlug(memberName ?? "");
  const fullDescription = member?.isSenate
    ? "United States Senate · Trading disclosures"
    : "House of Representatives · Trading disclosures";
  const description = isMobile ? undefined : fullDescription;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [activity, setActivity] = useState("all");
  useEffect(() => {
    setPage(0);
    setActivity("all");
  }, [memberName]);
  const name = memberSurname(member?.fmpName ?? "");
  const house = useHouseTradesByName({
    name,
    enabled: !!member && !member.isSenate,
  });
  const senate = useSenateTradesByName({ name, enabled: !!member?.isSenate });
  const query = member?.isSenate ? senate : house;
  const trades = useMemo(
    () =>
      newestTradesFirst(
        (query.data?.data ?? []).filter((trade) =>
          isMemberTrade(trade, member?.fmpName ?? ""),
        ),
      ),
    [query.data, member?.fmpName],
  );
  const filtered = useMemo(
    () =>
      trades.filter(
        (trade) =>
          activity === "all" || tradeDirection(trade.type) === activity,
      ),
    [trades, activity],
  );
  const latest = trades.reduce(
    (date, trade) =>
      dayjs(trade.disclosureDate).isAfter(dayjs(date)) || !date
        ? trade.disclosureDate
        : date,
    "",
  );
  const missing =
    isAxiosError(query.error) && query.error.response?.status === 404;
  const backTo = "/super-investors";
  if (!member)
    return (
      <InvestorWorkspace>
        <WorkspaceHeader
          title="Member not found"
          backTo={backTo}
          backState={{ investorSection: "congressional" }}
          backLabel="Congressional portfolios"
        />
        <WorkspaceState message="This congressional profile is unavailable." />
      </InvestorWorkspace>
    );
  return (
    <InvestorWorkspace tour="congressional-member-portfolio">
      <Box data-tour="congressional-member-header">
        <WorkspaceHeader
          title={member.name}
          mobileProfile
          description={description}
          backTo={backTo}
          backState={{ investorSection: "congressional" }}
          backLabel="Congressional portfolios"
          actions={
            !isMobile &&
            !query.isLoading &&
            !query.isError &&
            trades.length > 0 && (
              <DisclosureSummary count={trades.length} latest={latest} />
            )
          }
        >
          <Avatar
            {...getPersonPortraitImageProps(member.imageUrl, 32)}
            alt=""
            sx={{ width: 32, height: 32, borderRadius: 1.5 }}
          >
            {member.name[0]}
          </Avatar>
        </WorkspaceHeader>
      </Box>
      {query.isLoading && <WorkspaceState loading />}
      {!query.isLoading && query.isError && !missing && (
        <WorkspaceState
          message="Unable to load this member's disclosures."
          retry={() => {
            void query.refetch();
          }}
        />
      )}
      {!query.isLoading &&
        (missing || (!query.isError && trades.length === 0)) && (
          <WorkspaceState message="No trading disclosures available." />
        )}
      {!query.isError && !query.isLoading && trades.length > 0 && (
        <>
          <Box
            component="section"
            aria-label="Disclosures"
            data-disclosure-panel
            sx={{
              ...roundedPanelSx(theme.palette.mode === "dark"),
              scrollMarginTop: "calc(80px + env(safe-area-inset-top, 0px))",
              "& .disclosure-label": {
                fontSize: "0.8125rem",
                color: "text.secondary",
                textTransform: "none",
                letterSpacing: 0,
                mb: 0.5,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "space-between",
                mb: 1,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <SectionTitle count={filtered.length}>
                Trading activity
              </SectionTitle>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  flexWrap: "wrap",
                  ml: "auto",
                }}
              >
                <Select
                  size="small"
                  value={activity}
                  onChange={(event) => {
                    setActivity(event.target.value);
                    setPage(0);
                  }}
                  inputProps={{ "aria-label": "Transaction type" }}
                  sx={{
                    minHeight: 32,
                    "& .MuiSelect-select": { py: 0.5 },
                    "@media (pointer: coarse)": { minHeight: 44 },
                    minWidth: { xs: 112, sm: 140 },
                    fontSize: "0.8125rem",
                    borderRadius: 1.5,
                  }}
                >
                  <MenuItem value="all">All activity</MenuItem>
                  <MenuItem value="purchase">Purchases</MenuItem>
                  <MenuItem value="sale">Sales</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </Box>
            </Box>
            <CongressionalTradeTable
              trades={filtered.slice(page * pageSize, (page + 1) * pageSize)}
              showMember={false}
            />
            <DisclosurePagination
              page={page}
              pageSize={pageSize}
              count={
                filtered.slice(page * pageSize, (page + 1) * pageSize).length
              }
              total={filtered.length}
              hasNext={(page + 1) * pageSize < filtered.length}
              onPage={setPage}
              onPageSize={(size) => {
                setPageSize(size);
                setPage(0);
              }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Disclosed value ranges represent transactions, not current holdings.
            Activity is ordered by trade date.
          </Typography>
        </>
      )}
    </InvestorWorkspace>
  );
};
export default CongressionalMemberDetail;
